import { useCallback, useState } from 'react';
import styled from 'styled-components';
import Logo from '../svg/WaterlandLogo';
import { OptionType, SelectInputField } from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import { useRouter } from 'next/router';
import useGetPageSlugByLocale from '../../hooks/useGetPageSlugByLocale';
import Page from '../Page';

const options: OptionType[] = [
    { label: 'nl', value: 'nl' },
    { label: 'en', value: 'en' },
    { label: 'de', value: 'de' },
];

const Navigation = () => {
    const [isOpen, setIsOpen] = useState(false);
    const router = useRouter();
    const { getPageSlugByLocale } = useGetPageSlugByLocale();

    const getLink = useCallback(
        (lang: string) => {
            if (lang === router.locale) {
                return router.asPath;
            }
            const accommodationPageSlug = getPageSlugByLocale('ACCOMMODATION', lang);
            const activityPageSlug = getPageSlugByLocale('ACTIVITY', lang);
            const companyPageSlug = getPageSlugByLocale('COMPANY', lang);

            const pathParts = router.asPath.split('/');
            pathParts[0] = lang;
            switch (pathParts[1]) {
                case 'complex':
                    pathParts[1] = 'complex';
                    break;
                case 'accommodatie':
                case 'accommodation':
                case 'unterkunft':
                    pathParts[1] = accommodationPageSlug;
                    break;
                case 'activiteit':
                case 'activity':
                case 'aktivitat':
                    pathParts[1] = activityPageSlug;
                    break;
                case 'onderneming':
                case 'company':
                case 'firma':
                    pathParts[1] = companyPageSlug;
                    break;
                default:
                    break;
            }
            return pathParts.join('/');
        },
        [router.asPath, getPageSlugByLocale, router.locale]
    );

    return (
        <Page>
            <NavWrapper>
                <NavList
                    onClick={() => {
                        router.push(`/${router.locale}`);
                    }}
                >
                    <Red />
                    <LogoWrapper>
                        <Logo />
                    </LogoWrapper>
                </NavList>
                <LanguageWrapper onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
                    <StyledInput
                        hideChoices
                        styles={{
                            dropdownIndicator: base => ({
                                ...base,
                                display: 'none',
                            }),
                        }}
                        rounded
                        thinShadow
                        menuIsOpen={isOpen}
                        value={options.find(language => language.value === router.locale) ?? options[0]}
                        name="Language"
                        options={options.filter(language => language.value !== router.locale) ?? options}
                        isSearchable={false}
                        classNamePrefix="lang"
                        onChange={(option: OptionType) => {
                            const link = getLink(option.value);
                            if (link) {
                                window.location.href = link.startsWith('/') ? link : `/${link}`;
                            }
                        }}
                    />
                </LanguageWrapper>
            </NavWrapper>
        </Page>
    );
};

export default Navigation;

const Red = styled.button`
    background-color: #e8222a;
    cursor: pointer;
    width: 7rem;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
        width: 12rem;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 10rem;
    }
`;

const NavWrapper = styled.nav`
    display: flex;
    z-index: 100;
    position: fixed;
    top: 0;
    width: 100%;
    justify-content: space-between;
`;

const NavList = styled.ul`
    display: flex;
    padding: 0;
    margin: 0;
    cursor: pointer;
    height: 7rem;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
        height: 12rem;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        height: 10rem;
    }
`;

const StyledInput = styled(SelectInputField)`
    cursor: pointer;
    padding-top: 0.2rem;
    width: 50px;
    color: #0d72b9;
    font-size: 14px;
    text-transform: uppercase;
    border-color: white;

    [class^='lang__control'] {
        &.lang__control--menu-is-open {
            border-radius: 24px 24px 0 0;
            box-shadow: 0 0 0 1px #e6e9eb, inset 0 0 0 0 #e6e9eb;
        }
        box-shadow: 0 0 0 1px #e6e9eb, inset 0 0 0 0 #e6e9eb;
        padding-right: 0;
    }
    [class^='lang__menu'] {
        margin-top: 0;
        border-radius: 0 0 24px 24px;
        border: none;
        border-top: none;
        box-shadow: 0 0 0 1px #e6e9eb, inset 0 0 0 0 #e6e9eb;
    }

    [class^='lang__menu-list'] {
        padding-top: 0;
        padding-bottom: 0;
        border-radius: 0 0 24px 24px;
    }

    [class^='lang__option'] {
        color: #0d72b9;
        &:hover {
            background-color: red;
            color: white;
            text-decoration: underline;
        }
    }
`;

const LogoWrapper = styled.div`
    display: flex;
    padding-top: 0.5rem;
    padding-left: 0.5rem;
    background-image: linear-gradient(-45deg, #0000 15px, #fff 0);
    filter: drop-shadow(rgba(0, 0, 0, 0.25) 2px 0 2px);
`;

const LanguageWrapper = styled.div`
    display: flex;
    align-items: center;
    padding-right: 2.4rem;
`;
